// Wire-level rule type names match the backend `recommendations` API.
// The UI labels these as "BioMatch" rules.
export enum RuleType {
    GREATER_THAN = 'greater_than',
    GREATER_THAN_OR_EQUAL = 'gte',
    LESS_THAN = 'less_than',
    LESS_THAN_OR_EQUAL = 'lte',
    RANGE = 'range',
    OUTSIDE_RANGE = 'outside_range',
    EQUALS = 'equals',
    NOT_EQUALS = 'not_equals',
}

export enum RecommendationType {
    SUPPLEMENT = 'supplement',
    PROGRAM = 'program',
    CARE_PATHWAY = 'care_pathway',
    REFERRAL = 'referral',
    LIFESTYLE = 'lifestyle',
}

export enum RiskTier {
    CRITICAL = 'critical',
    HIGH = 'high',
    MODERATE = 'moderate',
    LOW = 'low',
}

/**
 * Who pays for the kit on enrollment. Mirrors the BE enum.
 */
export enum BillingType {
    EMPLOYER_FULL = 'employer_full',
    EMPLOYER_PARTIAL = 'employer_partial',
    PATIENT_FULL = 'patient_full',
}

/**
 * Where the rule's left-hand value comes from. Mirrors the BE enum.
 *  - BIOMARKER: a lab result, looked up by assay_catalog.code.
 *  - BIOMETRIC: a row in patient_biometrics keyed by measurement_key.
 *  - AGE:       computed from users.birthday at evaluation time.
 */
export enum RuleSource {
    BIOMARKER = 'biomarker',
    BIOMETRIC = 'biometric',
    AGE = 'age',
}

/**
 * BioMatch v2 type discriminator.
 *  - RANGE: globals (organization_id IS NULL). Rules + recommended
 *    assays + recommended products. Many fire at once; engine UNIONs
 *    their outputs into one Custom Lab Package.
 *  - CURATED: org-owned products. Full marketing record. Each fires
 *    as its own card.
 */
export enum BioMatchType {
    RANGE = 'range',
    CURATED = 'curated',
}

/** Rec-level: assay this rec wants to retest when it fires. */
export interface RecommendedAssay {
    assay_code: string;
    sort_order: number;
}

/** Rec-level: product to surface when this rec fires. */
export interface RecommendedProduct {
    storefront_product_id: number;
    sort_order: number;
}

export interface BiomarkerRule {
    /** Display label shown in the rule row (e.g. "HDL Cholesterol"). */
    test_type: string;
    /**
     * Canonical biomarker key — joins back to `assay_catalog.code`.
     * Required for `rule_source = BIOMARKER`; null for biometric/age.
     */
    assay_code?: string | null;
    rule_type: RuleType;
    /**
     * AND/OR grouping (DNF). Rules with the same group_index are AND-ed;
     * different group_index values are OR-ed. Default 0 means a single
     * AND group, which preserves the legacy behavior.
     */
    group_index: number;
    sort_order?: number;
    rule_source: RuleSource;
    /** Required when rule_source = BIOMETRIC; null otherwise. */
    measurement_key?: string | null;
    min_value?: number | null;
    max_value?: number | null;
    threshold_value?: number | null;
    rule_expression?: string;
    gender?: string | null;
}

export interface BioMatch {
    id: number;
    /**
     * NULL = global / system-default BioMatch visible to every org.
     * Tenant-owned BioMatches set this to the org id.
     */
    organization_id?: number | null;
    /** v2 discriminator. range = global; curated = org-owned. */
    bm_type: BioMatchType;
    name: string;
    enabled: boolean;
    content?: string;
    image: string;
    sup_type: string;
    price: number;
    members_price: number;
    url: string;
    // Curated-only marketing fields (NULL/empty for range recs)
    recommendation_type: RecommendationType;
    risk_tier: RiskTier | null;
    priority_weight: number | null;
    cta_button_label: string;
    cta_body_text: string;
    kit_sku: string;
    pathway_url: string;
    track_enrollment: boolean;
    billing_type: BillingType;
    employer_coverage_percent: number | null;
    biomarkers: BiomarkerRule[];
    /** Range recs: assays to retest when this rec fires. */
    recommended_assays: RecommendedAssay[];
    /** Range AND curated recs: products that ride along when this rec fires. */
    recommended_products: RecommendedProduct[];
}

export interface BiomarkerSearchResult {
    id: number;
    name: string;
    code: string | null;
}
